import type { Api } from '../../../lib/gramjs';
import type { TypedBroadcastChannel } from '../../../util/browser/multitab';
import type { ApiInitialArgs, ApiOnProgress, OnApiUpdate } from '../../types';
import type { LocalDb } from '../localDb';
import type { MethodArgs, MethodResponse, Methods } from '../methods/types';
import type { OriginPayload, ThenArg, WorkerMessageEvent } from './types';

import { DEBUG, IGNORE_UNHANDLED_ERRORS } from '../../../config';
import { logDebugMessage } from '../../../util/debugConsole';
import Deferred from '../../../util/Deferred';
import { getCurrentTabId, subscribeToMasterChange } from '../../../util/establishMultitabRole';
import generateUniqueId from '../../../util/generateUniqueId';
import { ACCOUNT_SLOT, DATA_BROADCAST_CHANNEL_NAME } from '../../../util/multiaccount';
import { pause, throttleWithTickEnd } from '../../../util/schedulers';
import { markStartupPerf } from '../../../util/startupPerf';
import { recordClientDiagnosticEvent } from '../../../util/clientDiagnostics';

type RequestState = {
  messageId: string;
  resolve: Function;
  reject: Function;
  callback?: AnyToVoidFunction;
  DEBUG_payload?: any;
  startedAt?: number;
};

const HEALTH_CHECK_TIMEOUT = 150;
const HEALTH_CHECK_MIN_DELAY = 5 * 1000; // 5 sec
const NO_QUEUE_BEFORE_INIT = new Set(['destroy']);

let worker: Worker | undefined;

const requestStates = new Map<string, RequestState>();
const requestStatesByCallback = new Map<AnyToVoidFunction, RequestState>();

let pendingPayloads: OriginPayload[] = [];

const savedLocalDb: LocalDb = {
  chats: {},
  users: {},
  documents: {},
  stickerSets: {},
  photos: {},
  webDocuments: {},
  commonBoxState: {},
  channelPtsById: {},
};

let isMasterTab = true;
subscribeToMasterChange((isMasterTabNew) => {
  isMasterTab = isMasterTabNew;
});

let isFirstWorkerMessageMarked = false;
let isFirstApiResponseMarked = false;

const channel = new BroadcastChannel(DATA_BROADCAST_CHANNEL_NAME) as TypedBroadcastChannel;

const postMessagesOnTickEnd = throttleWithTickEnd(() => {
  const payloads = pendingPayloads;
  pendingPayloads = [];
  worker?.postMessage({ payloads });
});

function postMessageOnTickEnd(payload: OriginPayload) {
  pendingPayloads.push(payload);
  postMessagesOnTickEnd();
}

export function initApiOnMasterTab(initialArgs: ApiInitialArgs) {
  channel.postMessage({
    type: 'initApi',
    token: getCurrentTabId(),
    initialArgs,
  });
}

let updateCallback: OnApiUpdate;

let localApiRequestsQueue: { fnName: any; args: any; deferred: Deferred<any> }[] = [];
let apiRequestsQueue: { fnName: any; args: any; deferred: Deferred<any> }[] = [];
let isInited = false;
let isInitializing = false;
let isIosPlatform = false;

export function initApi(onUpdate: OnApiUpdate, initialArgs: ApiInitialArgs) {
  updateCallback = onUpdate;
  isIosPlatform = initialArgs.platform === 'iOS';

  if (!isMasterTab) {
    initApiOnMasterTab(initialArgs);
    return Promise.resolve();
  }

  if (isInitializing) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.warn('[Connector] initApi() already in progress, skipping');
    }
    return Promise.resolve();
  }

  if (!worker) {
    markStartupPerf('worker_boot_start');

    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('>>> START LOAD WORKER');
    }

    const params = new URLSearchParams();
    if (ACCOUNT_SLOT) {
      params.set('account', String(ACCOUNT_SLOT));
    }

    worker = new Worker(new URL('./worker.ts', import.meta.url), {
      name: params.toString(),
    });
    subscribeToWorker(onUpdate);

    // 监听页面可见性变化，Android 后台恢复时通知 Worker 强制重连
    // Worker 内部无法访问 document，必须在主线程监听后转发
    document.addEventListener('visibilitychange', handleVisibilityChangeForWorker);
    window.addEventListener('pageshow', handlePageShowForWorker);
    window.addEventListener('resume', handlePageResumeForWorker);

    if (isIosPlatform) {
      setupIosHealthCheck();
    }
  }

  isInitializing = true;
  return makeRequest({
    type: 'initApi',
    args: [initialArgs, savedLocalDb],
  }).then(() => {
    markFirstApiResponse({ type: 'initApi' });
    isInited = true;
    isInitializing = false;

    apiRequestsQueue.forEach((request) => {
      callApi(request.fnName, ...request.args)
        .then(request.deferred.resolve)
        .catch(request.deferred.reject);
    });
    apiRequestsQueue = [];

    localApiRequestsQueue.forEach((request) => {
      callApiLocal(request.fnName, ...request.args)
        .then(request.deferred.resolve)
        .catch(request.deferred.reject);
    });
    localApiRequestsQueue = [];
  }).catch((err: any) => {
    isInitializing = false;
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.error('[Connector] initApi failed:', err);
    }
    throw err;
  });
}

export async function destroyApi() {
  if (!worker) return;

  try {
    await makeRequest({ type: 'callMethod', name: 'destroy', args: [true, true] });
  } catch (_) {
    // ignore - worker may already be dead
  }

  worker.terminate();
  worker = undefined;
  isInited = false;
  isInitializing = false;
  apiRequestsQueue = [];
  localApiRequestsQueue = [];
  requestStates.clear();
  requestStatesByCallback.clear();
  pendingPayloads = [];

  document.removeEventListener('visibilitychange', handleVisibilityChangeForWorker);
  window.removeEventListener('pageshow', handlePageShowForWorker);
  window.removeEventListener('resume', handlePageResumeForWorker);

  // 清理安成待发的重连定时器
  if (pendingReconnectTimer) {
    clearTimeout(pendingReconnectTimer);
    pendingReconnectTimer = null;
  }

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[Connector] Worker destroyed and states reset');
  }
}

export function updateLocalDb(name: keyof LocalDb, prop: string, value: any) {
  savedLocalDb[name][prop] = value;
}

export function updateFullLocalDb(initial: LocalDb) {
  Object.assign(savedLocalDb, initial);
}

export function callApiOnMasterTab(payload: any) {
  channel.postMessage({
    type: 'callApi',
    token: getCurrentTabId(),
    ...payload,
  });
}

export function setShouldEnableDebugLog(value: boolean) {
  return makeRequest({
    type: 'toggleDebugMode',
    isEnabled: value,
  });
}

/*
 * Call a worker method on this tab's worker, without transferring to master tab
 * Mostly needed to disconnect worker when re-electing master
 */
export function callApiLocal<T extends keyof Methods>(fnName: T, ...args: MethodArgs<T>) {
  if (!isInited) {
    if (NO_QUEUE_BEFORE_INIT.has(fnName)) {
      return Promise.resolve(undefined) as MethodResponse<T>;
    }

    const deferred = new Deferred();
    localApiRequestsQueue.push({ fnName, args, deferred });

    return deferred.promise as MethodResponse<T>;
  }

  const promise = makeRequest({
    type: 'callMethod',
    name: fnName,
    args,
  });

  // Some TypeScript magic to make sure `VirtualClass` is never returned from any method
  if (DEBUG) {
    (async () => {
      try {
        type ForbiddenTypes =
          Api.VirtualClass<any>
          | (Api.VirtualClass<any> | undefined)[];
        type ForbiddenResponses =
          ForbiddenTypes
          | (AnyLiteral & { [k: string]: ForbiddenTypes });

        // Unwrap all chained promises
        const response = await promise;
        // Make sure responses do not include `VirtualClass` instances
        const allowedResponse: Exclude<typeof response, ForbiddenResponses> = response;
        // Suppress "unused variable" constraint
        void allowedResponse;
      } catch (err) {
        // Do noting
      }
    })();
  }

  return promise as MethodResponse<T>;
}

export function callApi<T extends keyof Methods>(fnName: T, ...args: MethodArgs<T>) {
  if (!isInited && isMasterTab) {
    if (NO_QUEUE_BEFORE_INIT.has(fnName)) {
      return Promise.resolve(undefined) as MethodResponse<T>;
    }

    const deferred = new Deferred();
    apiRequestsQueue.push({ fnName, args, deferred });

    return deferred.promise as MethodResponse<T>;
  }

  const promise = isMasterTab ? makeRequest({
    type: 'callMethod',
    name: fnName,
    args,
  }) : makeRequestToMaster({
    name: fnName,
    args,
  });

  // Some TypeScript magic to make sure `VirtualClass` is never returned from any method
  if (DEBUG) {
    (async () => {
      try {
        type ForbiddenTypes =
          Api.VirtualClass<any>
          | (Api.VirtualClass<any> | undefined)[];
        type ForbiddenResponses =
          ForbiddenTypes
          | (AnyLiteral & { [k: string]: ForbiddenTypes });

        // Unwrap all chained promises
        const response = await promise;
        // Make sure responses do not include `VirtualClass` instances
        const allowedResponse: Exclude<typeof response, ForbiddenResponses> = response;
        // Suppress "unused variable" constraint
        void allowedResponse;
      } catch (err) {
        // Do noting
      }
    })();
  }

  return promise as MethodResponse<T>;
}

export function cancelApiProgress(progressCallback: ApiOnProgress) {
  progressCallback.isCanceled = true;

  const { messageId } = requestStatesByCallback.get(progressCallback) || {};
  if (!messageId) {
    return;
  }

  if (isMasterTab) {
    cancelApiProgressMaster(messageId);
  } else {
    channel.postMessage({
      type: 'cancelApiProgress',
      token: getCurrentTabId(),
      messageId,
    });
  }
}

export function cancelApiProgressMaster(messageId: string) {
  postMessageOnTickEnd({
    type: 'cancelProgress',
    messageId,
  });
}

function subscribeToWorker(onUpdate: OnApiUpdate) {
  worker?.addEventListener('message', ({ data }: WorkerMessageEvent) => {
    if (!isFirstWorkerMessageMarked) {
      isFirstWorkerMessageMarked = true;
      markStartupPerf('worker_first_message');
    }

    data?.payloads.forEach((payload) => {
      if (payload.type === 'updates') {
        recordClientDiagnosticEvent({
          type: 'worker_update_received',
          reason: `count:${payload.updates.length}`,
        });
        // eslint-disable-next-line @typescript-eslint/naming-convention
        let DEBUG_startAt: number | undefined;
        DEBUG_startAt = performance.now();

        payload.updates.forEach(onUpdate);

        const duration = performance.now() - DEBUG_startAt!;
        recordClientDiagnosticEvent({
          type: 'main_update_applied',
          reason: `count:${payload.updates.length}`,
          duration,
        });

        if (DEBUG) {
          if (duration > 5) {
            // eslint-disable-next-line no-console
            console.warn(`[API] Slow updates processing: ${payload.updates.length} updates in ${duration} ms`);
          }
        }
      } else if (payload.type === 'methodResponse') {
        handleMethodResponse(payload);
      } else if (payload.type === 'methodCallback') {
        handleMethodCallback(payload);
      } else if (payload.type === 'unhandledError') {
        const raw = payload.error?.message;
        const message = typeof raw === 'string' ? raw : JSON.stringify(payload.error ?? raw ?? 'Unknown');
        if (message && IGNORE_UNHANDLED_ERRORS.has(message)) return;
        throw new Error(message);
      } else if (payload.type === 'sendBeacon') {
        navigator.sendBeacon(payload.url, payload.data);
      } else if (payload.type === 'debugLog') {
        logDebugMessage(payload.level, ...payload.args);
      } else if (payload.type === 'diagnosticEvent') {
        recordClientDiagnosticEvent(payload.event);
      }
    });
  });
}

export function handleMethodResponse(data: {
  messageId: string;
  response?: ThenArg<MethodResponse<keyof Methods>>;
  error?: { message: string };
}) {
  const requestState = requestStates.get(data.messageId);
  if (requestState) {
    recordMethodDiagnostic(requestState, data.error?.message);
    if (data.error) {
      const msg = typeof data.error.message === 'string' ? data.error.message : JSON.stringify(data.error);
      requestState.reject(new Error(msg));
    } else {
      markFirstApiResponse(requestState.DEBUG_payload && 'name' in requestState.DEBUG_payload
        ? { name: requestState.DEBUG_payload.name }
        : undefined);
      requestState.resolve(data.response);
    }
  }
}

export function handleMethodCallback(data: {
  messageId: string;
  callbackArgs: any[];
}) {
  requestStates.get(data.messageId)?.callback?.(...data.callbackArgs);
}

function makeRequestToMaster(message: {
  messageId?: string;
  name: keyof Methods;
  args: MethodArgs<keyof Methods>;
  withCallback?: boolean;
}) {
  const messageId = generateUniqueId();
  const payload = {
    messageId,
    ...message,
  };

  const requestState = { messageId, startedAt: Date.now() } as RequestState;

  // Re-wrap type because of `postMessage`
  const promise: Promise<MethodResponse<keyof Methods>> = new Promise((resolve, reject) => {
    Object.assign(requestState, { resolve, reject });
  });

  if ('args' in payload && 'name' in payload && typeof payload.args[1] === 'function') {
    payload.withCallback = true;

    const callback = payload.args.pop() as AnyToVoidFunction;
    requestState.callback = callback;
    requestStatesByCallback.set(callback, requestState);
  }

  requestState.DEBUG_payload = { type: 'callMethod', name: payload.name };
  requestStates.set(messageId, requestState);

  promise
    .catch(() => undefined)
    .finally(() => {
      requestStates.delete(messageId);

      if (requestState.callback) {
        requestStatesByCallback.delete(requestState.callback);
      }
    });

  callApiOnMasterTab(payload);

  return promise;
}

function makeRequest(message: OriginPayload) {
  const messageId = generateUniqueId();
  const payload: OriginPayload = {
    messageId,
    ...message,
  };

  const requestState = { messageId, startedAt: Date.now() } as RequestState;

  // Re-wrap type because of `postMessage`
  const promise: Promise<MethodResponse<keyof Methods>> = new Promise((resolve, reject) => {
    Object.assign(requestState, { resolve, reject });
  });

  if ('args' in payload && 'name' in payload && typeof payload.args[1] === 'function') {
    payload.withCallback = true;

    const callback = payload.args.pop() as AnyToVoidFunction;
    requestState.callback = callback;
    requestStatesByCallback.set(callback, requestState);
  }

  requestState.DEBUG_payload = payload;

  requestStates.set(messageId, requestState);

  promise
    .catch(() => undefined)
    .finally(() => {
      requestStates.delete(messageId);

      if (requestState.callback) {
        requestStatesByCallback.delete(requestState.callback);
      }
    });

  postMessageOnTickEnd(payload);

  return promise;
}

function recordMethodDiagnostic(requestState: RequestState, error?: string) {
  const payload = requestState.DEBUG_payload;
  if (!payload || payload.type !== 'callMethod' || !requestState.startedAt) return;

  const duration = Date.now() - requestState.startedAt;
  const name = 'name' in payload ? String(payload.name) : '';

  if (name === 'sendMessage') {
    recordClientDiagnosticEvent({
      type: 'send_message_ack',
      duration,
      success: !error,
      error,
    });
  } else if (name === 'requestChannelDifference') {
    recordClientDiagnosticEvent({
      type: 'get_channel_difference',
      reason: 'manual_request',
      duration,
      success: !error,
      error,
    });
  }
}

function markFirstApiResponse(data?: Record<string, any>) {
  if (isFirstApiResponseMarked) return;

  isFirstApiResponseMarked = true;
  markStartupPerf('api_first_response', data);
}

const startedAt = Date.now();

// 主线程页面可见性监听 → 通知 Worker 强制重连（解决 Android 后台恢复无法重连问题）
let lastHiddenAt = 0;
const RECONNECT_AFTER_HIDDEN_MS = 3000; // 后台超过 3 秒才触发重连

function sendForceReconnectToWorker(
  reason = 'manual',
  options: { probeFirst?: boolean; fallbackOnCatchUpFailure?: boolean } = {},
) {
  if (!worker) return;
  postMessageOnTickEnd({
    type: 'forceReconnect',
    reason,
    ...options,
  });
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[Connector] Sent forceReconnect to worker', { reason, ...options });
  }
}

export function forceReconnectApi() {
  sendForceReconnectToWorker('manual', { probeFirst: true, fallbackOnCatchUpFailure: true });
}

// 同一次前后台切换可能同时触发 visibilitychange / pageshow / resume，
// 三个事件都会发一条 forceReconnect。打包去抖避免 Worker 重复重连。
let pendingReconnectTimer: ReturnType<typeof setTimeout> | null = null;
const RECONNECT_DEBOUNCE_MS = 500;

function scheduleForceReconnect(reason: string) {
  if (pendingReconnectTimer) {
    clearTimeout(pendingReconnectTimer);
  }
  pendingReconnectTimer = setTimeout(() => {
    pendingReconnectTimer = null;
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log(`[Connector] forceReconnect dispatched, reason=${reason}`);
    }
    sendForceReconnectToWorker(reason, {
      // iOS WebView 唤醒后的旧 socket 经常处于"半活"状态，直接重建比探活误判更可靠。
      probeFirst: !isIosPlatform,
      fallbackOnCatchUpFailure: true,
    });
  }, RECONNECT_DEBOUNCE_MS);
}

function handleVisibilityChangeForWorker() {
  if (document.hidden) {
    lastHiddenAt = Date.now();
    return;
  }

  // 只有后台时间超过阈值才重连，避免短暂切换触发不必要重连
  if (Date.now() - lastHiddenAt >= RECONNECT_AFTER_HIDDEN_MS) {
    scheduleForceReconnect('visibilitychange');
  }
}

function handlePageShowForWorker(e: PageTransitionEvent) {
  // persisted=true 说明是从 bfcache 恢复，连接必然已断
  if (e.persisted || lastHiddenAt > 0) {
    scheduleForceReconnect(e.persisted ? 'pageshow-bfcache' : 'pageshow');
  }
}

function handlePageResumeForWorker() {
  scheduleForceReconnect('resume');
}

// Workaround for iOS sometimes stops interacting with worker
function setupIosHealthCheck() {
  window.addEventListener('focus', () => {
    void ensureWorkerPing();
    // Sometimes a single check is not enough
    setTimeout(() => ensureWorkerPing(), 1000);
  });
}

async function ensureWorkerPing() {
  let isResolved = false;

  try {
    await Promise.race([
      makeRequest({ type: 'ping' }),
      pause(HEALTH_CHECK_TIMEOUT)
        .then(() => (isResolved ? undefined : Promise.reject(new Error('HEALTH_CHECK_TIMEOUT')))),
    ]);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);

    if (Date.now() - startedAt >= HEALTH_CHECK_MIN_DELAY) {
      worker?.terminate();
      worker = undefined;
      isInited = false;
      updateCallback({ '@type': 'requestReconnectApi' });
    }
  } finally {
    isResolved = true;
  }
}
